/**
 * Costruzione client-side del file di un report (PDF o Excel) a partire dalla
 * {@link ReportSpec} che `ReportBuilderView` (`@slims/ui`) compone dalle scelte
 * dell'utente. Vive nell'app, non in `@slims/ui`: qui stanno i blob, gli object
 * URL e l'apertura di finestre — effetti che la vista non può avere.
 *
 * - PDF: `jspdf`, si apre nel viewer del browser e si scarica come `.pdf`.
 * - Excel: `xlsx` (SheetJS), `.xlsx` vero; i browser non lo mostrano inline,
 *   quindi "apri" avvia il download e lo apre il sistema operativo.
 */
import { IT_INT } from '@slims/shared/constants';
import {
	REPORT_VALUE_UNAVAILABLE,
	type ReportFormat,
	type ReportSpec,
	type ReportSpecSection,
} from '@slims/ui/organisms';
import { jsPDF } from 'jspdf';
import * as XLSX from 'xlsx';

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/**
 * Avviso quando nessun blocco ha un valore (INB-358): il documento mostra la
 * struttura del report, non i suoi numeri. È il caso dei report salvati prima
 * che i blocchi fossero collegati ai dati (INB-380).
 */
const DRAFT_NOTICE =
	'Bozza: i valori dei blocchi non sono ancora collegati ai dati della configurazione.';

/** Avviso quando solo alcuni blocchi hanno un valore (INB-380). */
const PARTIAL_NOTICE =
	'Alcuni blocchi non hanno ancora un dato per il periodo: sono segnati «dato non ancora disponibile».';

/** L'avviso da stampare, o `null` se ogni blocco ha il suo valore. */
function reportNotice(spec: ReportSpec): string | null {
	const blocks = spec.sections.flatMap((s) => s.blocks);
	const missing = blocks.filter((b) => b.value == null).length;
	if (missing === 0) return null;
	return missing === blocks.length ? DRAFT_NOTICE : PARTIAL_NOTICE;
}

/**
 * Il valore di un blocco, mai un numero di esempio. I report salvati in
 * `localStorage` prima di INB-358 portano ancora `sample` con valori inventati:
 * qui non vengono letti, e cadono sul testo di dato non disponibile.
 */
function blockValue(block: ReportSpecSection['blocks'][number]): string {
	return block.value ?? REPORT_VALUE_UNAVAILABLE;
}

export function formatLabel(format: ReportFormat): string {
	return format === 'excel' ? 'Excel' : 'PDF';
}

/** Nome file: titolo + periodo, ripulito, con l'estensione del formato. */
export function reportFilename(spec: ReportSpec): string {
	const base =
		`${spec.title} ${spec.periodLabel}`
			.normalize('NFD')
			.replace(new RegExp('[\\u0300-\\u036f]', 'g'), '')
			.replace(/[^a-zA-Z0-9]+/g, '-')
			.replace(/^-+|-+$/g, '')
			.toLowerCase() || 'report';
	return `${base}.${spec.format === 'excel' ? 'xlsx' : 'pdf'}`;
}

/**
 * I report salvati prima di INB-358 chiamano la configurazione «<nome> S.C.»:
 * il suffisso lo aggiungeva la vista d'ufficio, dichiarando cooperativa
 * qualunque CER. Qui lo si toglie da soggetto e intestazione.
 */
function withoutLegacySuffix(spec: ReportSpec): ReportSpec {
	if (spec.scope === 'socio' || !spec.subject.endsWith(' S.C.')) return spec;
	const subject = spec.subject.slice(0, -' S.C.'.length);
	return {
		...spec,
		subject,
		meta: spec.meta.startsWith(spec.subject) ? subject + spec.meta.slice(spec.subject.length) : spec.meta,
	};
}

function buildPdf(input: ReportSpec): Blob {
	const spec = withoutLegacySuffix(input);
	const doc = new jsPDF({ unit: 'pt', format: 'a4' });
	const margin = 48;
	const pageWidth = doc.internal.pageSize.getWidth();
	const pageHeight = doc.internal.pageSize.getHeight();
	let y = margin;

	const ensureSpace = (needed: number) => {
		if (y + needed > pageHeight - margin) {
			doc.addPage();
			y = margin;
		}
	};

	if (spec.flags.logo && spec.logo) {
		// Il logo della CER (INB-380), in proporzione, alto al massimo 32 pt.
		const h = Math.min(32, spec.logo.height);
		const w = (spec.logo.width / spec.logo.height) * h;
		const format = spec.logo.dataUrl.startsWith('data:image/png') ? 'PNG' : 'JPEG';
		doc.addImage(spec.logo.dataUrl, format, pageWidth - margin - w, margin - h + 8, w, h);
	} else if (spec.flags.logo) {
		doc.setFont('courier', 'bold');
		doc.setFontSize(11);
		doc.text('SLIMS', pageWidth - margin, margin, { align: 'right' });
	}

	doc.setFont('helvetica', 'bold');
	doc.setFontSize(18);
	doc.text(spec.title, margin, y);
	y += 22;

	doc.setFont('helvetica', 'normal');
	doc.setFontSize(10);
	doc.setTextColor(120);
	doc.text(spec.meta, margin, y);
	y += 16;
	const notice = reportNotice(spec);
	if (notice) {
		doc.setFont('helvetica', 'italic');
		doc.text(notice, margin, y);
		doc.setFont('helvetica', 'normal');
	}
	doc.setTextColor(0);
	y += 22;

	doc.setDrawColor(210);
	doc.line(margin, y, pageWidth - margin, y);
	y += 22;

	for (const section of spec.sections) {
		ensureSpace(40);
		doc.setFont('helvetica', 'bold');
		doc.setFontSize(12);
		doc.text(section.label, margin, y);
		y += 16;

		doc.setFont('helvetica', 'normal');
		doc.setFontSize(10);
		for (const block of section.blocks) {
			const wrapped: string[] = doc.splitTextToSize(
				`•  ${block.title} - ${blockValue(block)}`,
				pageWidth - margin * 2,
			);
			ensureSpace(wrapped.length * 13 + 4);
			doc.text(wrapped, margin, y);
			y += wrapped.length * 13 + 4;
		}
		y += 12;
	}

	if (spec.flags.firma) {
		ensureSpace(64);
		y = Math.max(y, pageHeight - margin - 48);
		doc.setDrawColor(180);
		doc.line(margin, y, margin + 180, y);
		y += 12;
		doc.setFontSize(9);
		doc.setTextColor(120);
		doc.text('Firma', margin, y);
		doc.setTextColor(0);
	}

	const total = doc.getNumberOfPages();
	for (let i = 1; i <= total; i++) {
		doc.setPage(i);
		doc.setFontSize(8);
		doc.setTextColor(150);
		doc.text(`${spec.subject} · ${spec.periodLabel} · pagina ${i}/${total}`, margin, pageHeight - 24);
		doc.setTextColor(0);
	}

	return doc.output('blob');
}

/** Le righe del foglio Excel: esportata perché è la parte da verificare, senza il file. */
export function reportSheetRows(input: ReportSpec): (string | number)[][] {
	const spec = withoutLegacySuffix(input);
	const rows: (string | number)[][] = [
		[spec.title],
		[spec.meta],
		[reportNotice(spec) ?? ''],
		[],
		['Sezione', 'Blocco', 'Dettaglio', 'Valore'],
	];
	for (const section of spec.sections) {
		for (const block of section.blocks) {
			rows.push([section.label, block.title, block.sub, blockValue(block)]);
		}
	}
	rows.push([]);
	rows.push(['Generato il', new Date(spec.generatedAt).toLocaleString('it-IT')]);
	rows.push(['Pagine stimate (PDF)', spec.pages]);

	return rows;
}

function buildXlsx(spec: ReportSpec): Blob {
	const rows = reportSheetRows(spec);
	const ws = XLSX.utils.aoa_to_sheet(rows);
	ws['!cols'] = [{ wch: 22 }, { wch: 32 }, { wch: 34 }, { wch: 40 }];
	const wb = XLSX.utils.book_new();
	XLSX.utils.book_append_sheet(wb, ws, 'Report');
	const out = XLSX.write(wb, { type: 'array', bookType: 'xlsx' }) as ArrayBuffer;
	return new Blob([out], { type: XLSX_MIME });
}

export function buildReportBlob(spec: ReportSpec): Blob {
	return spec.format === 'excel' ? buildXlsx(spec) : buildPdf(spec);
}

function withObjectUrl(blob: Blob, use: (url: string) => void): void {
	const url = URL.createObjectURL(blob);
	use(url);
	// Lasciamo un margine perché il viewer PDF / il download della nuova scheda
	// finiscano di leggere l'URL prima di revocarlo.
	setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** Scarica il report come file. */
export function downloadReportBlob(spec: ReportSpec): void {
	withObjectUrl(buildReportBlob(spec), (url) => {
		const a = document.createElement('a');
		a.href = url;
		a.download = reportFilename(spec);
		document.body.appendChild(a);
		a.click();
		a.remove();
	});
}

/**
 * Apre il report in una nuova finestra: il PDF resta nel viewer del browser,
 * l'Excel viene scaricato dalla nuova scheda. Se il popup è bloccato, ripiega
 * sul download.
 */
/**
 * URL locale (`blob:`) del PDF di un report, da mostrare nella finestra di
 * anteprima dell'app (INB-381). Chi lo crea lo revoca con
 * `URL.revokeObjectURL` quando chiude la finestra.
 */
export function reportPdfObjectUrl(spec: ReportSpec): string {
	return URL.createObjectURL(buildReportBlob({ ...spec, format: 'pdf' }));
}
